import {
  FluidButtonType,
  FluidContextMenuItemType,
  FluidContextMenuLink,
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidElementType,
  FluidMenuSectionStyle,
  FluidProgressState,
  FluidSize,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { CloseButton } from '../../../common/fluid-common-button.components';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  FluidContextMenu,
  FluidContextMenuTranslationOptions,
} from '../fluid-context-menu';

export interface ContextMenuProps extends FluidFunctionalComponentProps {
  host: FluidContextMenu;
  hasToggles: boolean;
  section?: FluidContextMenuSection;
  isLast?: boolean;
  link?: FluidContextMenuLink;
  toggle?: FluidContextMenuToggle;
  closeLabel: string;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidContextMenuTranslationOptions;
}

// ================================================================ //
// -- Public Functional Components

/**
 * All sections within the Context Menu, plus an optional "menu actions"
 * section containing a "close" button.
 */
export const ContextMenuContent: FunctionalComponent<ContextMenuProps> = (
  props
) => {
  const { host, i18nStore } = props;
  const lastIndex = host._menuSections?.length - 1;

  return [
    host._menuSections.map((section: FluidContextMenuSection, idx: number) => {
      return (
        <ContextMenuSection
          section={section}
          i18nStore={i18nStore}
          isLast={idx === lastIndex}
          {...props}
        />
      );
    }),
    <ContextMenuActions {...props} />,
  ];
};

// ================================================================ //
// -- Private Functional Components

/**
 * Menu section that contains links, Toggles, or a named <slot>.
 * Also renders a bottom divider if section isn't last in the menu.
 */
export const ContextMenuSection: FunctionalComponent<ContextMenuProps> = (
  props
) => {
  const { section, isLast, i18nStore } = props;

  const title =
    i18nStore?.translate(section.fluidTranslate?.title) || section.title;
  return [
    <div class='menu-section'>
      {title && (
        <span id={`${section.id}-title`} class='menu-title'>
          {title}
        </span>
      )}
      {renderSectionContent(section, props)}
    </div>,
    !isLast && <hr class='menu-divider' />,
  ];
};

/**
 * Determines the Context Menu section type & returns the appropriate
 * functional component.
 * @param section - the FluidContextMenuSection to render
 * @param props - Props from the calling component
 */
const renderSectionContent = (
  section: FluidContextMenuSection,
  props: ContextMenuProps
) => {
  switch (section.type) {
    case FluidContextMenuItemType.LINK:
    case FluidContextMenuItemType.TOGGLE:
      return <ContextMenuList {...props} />;
    case FluidContextMenuItemType.TOGGLE_GROUP:
      return <ContextMenuToggleGroup {...props} />;
    case FluidContextMenuItemType.SLOT:
      return <ContextMenuSlot {...props} />;
  }
};

/**
 * List container for links or Toggles within a ContextMenuSection.
 */
const ContextMenuList: FunctionalComponent<ContextMenuProps> = (props) => {
  const { section } = props;
  const items = getMenuItems(section) as never[];
  return (
    <ul
      role='menu'
      class='menu-list'
      aria-labelledby={section.title ? `${section.id}-title` : null}
      style={
        !!section.sectionHeight && {
          maxHeight: section.sectionHeight,
          overflowY: 'auto',
        }
      }>
      {items.map((item) => {
        switch (section.type) {
          case FluidContextMenuItemType.LINK:
            return <ContextMenuLink link={item} {...props} />;
          case FluidContextMenuItemType.TOGGLE:
            return <ContextMenuToggle toggle={item} {...props} />;
        }
      })}
    </ul>
  );
};

/**
 * Link (with or without icons) within ContextMenuList.
 */
const ContextMenuLink: FunctionalComponent<ContextMenuProps> = (props) => {
  const { host, link, i18nStore } = props;
  const isAnchor = !!link.route || !!link.externalLink;
  const LinkTag = isAnchor ? FluidElementType.ANCHOR : FluidElementType.BUTTON;

  return (
    <li role='none' class='menu-item'>
      <LinkTag
        role='menuitem'
        class='menu-item-link'
        href={isAnchor ? link.route ?? link.externalLink : null}
        onClick={(event) => host.handleLinkClick(link, event)}>
        <ContextMenuIcon position='before' {...props} />
        <span class='link-label'>
          {i18nStore?.translate(link.fluidTranslate?.label) || link.label}
        </span>
        <ContextMenuIcon position='after' {...props} />
      </LinkTag>
    </li>
  );
};

/**
 * ContextMenuLink Icon (before or after label).
 */
const ContextMenuIcon: FunctionalComponent<ContextMenuProps> = (props) => {
  const { host, link, position } = props;

  const iconConfig = position === 'before' ? link.iconBefore : link.iconAfter;
  if (!iconConfig) return;

  return (
    <span class={`link-icon icon-${position}`}>
      <fluid-icon
        key={`${link.label}-icon-${position}--${iconConfig.key}`}
        config={{
          size: FluidSize.SMALL,
          popoverIgnoreClippingBoundaries: false,
          ...iconConfig,
          theme: host.theme,
          overrideGlobalTheme: true,
        }}>
        {!!iconConfig.slotName && (
          <slot name={iconConfig.slotName} slot={iconConfig.slotName} />
        )}
      </fluid-icon>
    </span>
  );
};

/**
 * FLUID Toggle (checkbox, flag, link, switch, custom) within ContextMenuList.
 * If `toggleType` isn't specified, Toggle will be a standard checkbox.
 */
const ContextMenuToggle: FunctionalComponent<ContextMenuProps> = (props) => {
  const { host, toggle } = props;

  return (
    <li role='none' class='menu-item'>
      <fluid-toggle
        role='menuitem'
        class='menu-item-toggle'
        config={toggle}
        toggled={toggle.selected}
        initialValue={toggle.selected}
        asMenuItem={true}
        theme={host.theme}
        overrideGlobalTheme={true}
        onValueChange={(event) =>
          host.handleToggleChange(event.detail.value, toggle)
        }
      />
    </li>
  );
};

/**
 * FLUID Toggle Group within a ContextMenuSection.
 */
const ContextMenuToggleGroup: FunctionalComponent<ContextMenuProps> = (
  props
) => {
  const { host, section, i18nStore } = props;
  const toggles = getMenuItems(section) as FluidContextMenuToggle[];
  const toggleAllLabel =
    i18nStore?.translate(section.fluidTranslate?.toggleAllLabel) ||
    'Select All';

  // Map "selected" property => "toggled" so that it's recognized by Toggle Group
  const toggleOptions = toggles.map(({ selected, ...config }) => ({
    ...config,
    toggled: !!selected,
  }));

  const toggleAllValue = toggles.every((toggle) => toggle.selected)
    ? true
    : toggles.every((toggle) => !toggle.selected)
    ? false
    : FluidProgressState.INDETERMINATE;

  return (
    <fluid-toggle-group
      style={{ maxHeight: section.sectionHeight }}
      ref={(el) => host.setToggleGroupRef(el, section.id)}
      toggleOptions={toggleOptions}
      toggleAllValue={toggleAllValue}
      toggleAllLabel={toggleAllLabel}
      asMenuSection={true}
      theme={host.theme}
      overrideGlobalTheme={true}
      onToggleState={(event) =>
        host.handleToggleGroupChange(event.detail, toggles)
      }
      class={{
        'menu-toggle-group': true,
        'section-height-applied': !!section.sectionHeight,
      }}
    />
  );
};

/**
 * Custom named slot within ContextMenuSection.
 */
export const ContextMenuSlot: FunctionalComponent<ContextMenuProps> = (
  props
) => {
  const { section } = props;

  return (
    <div
      class={{
        'menu-slot': true,
        'offset-padding': section.removeSlotPadding,
      }}
      style={
        !!section.sectionHeight && {
          maxHeight: section.sectionHeight,
          overflowY: 'auto',
        }
      }>
      <slot name={section.slotName} />
    </div>
  );
};

/**
 * "Close" Button at bottom of Context Menu OR "x" Button in upper
 * right corner of Context Menu. Only rendered if menu contains
 * Toggles & "outsideClickClose" is NOT true.
 */
export const ContextMenuActions: FunctionalComponent<ContextMenuProps> = (
  props: ContextMenuProps
) => {
  const { host, hasToggles, closeLabel, i18nStore, fluidTranslate } = props;
  const isCompact = host.menuSectionStyle === FluidMenuSectionStyle.INLINE;

  if (!host.displayMenuActions && (!hasToggles || host.outsideClickClose))
    return;

  return (
    <div
      class={{
        'menu-actions': true,
        compact: isCompact,
      }}>
      {!isCompact ? (
        <fluid-button
          label={
            i18nStore?.translate(fluidTranslate?.closeButton?.label) ||
            closeLabel
          }
          type={FluidButtonType.PRIMARY}
          onClick={() => host.toggleMenu()}
          theme={host.theme}
          overrideGlobalTheme={true}
          removeMargin={true}
          small={true}
        />
      ) : (
        <CloseButton
          title={
            i18nStore?.translate(fluidTranslate?.closeButton?.label) ||
            closeLabel
          }
          theme={host.theme}
          onClick={() => host.toggleMenu()}
        />
      )}
    </div>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Returns links or toggles (or whatever) based on section type so
 * calling functions can have a generified array to work with.
 * @param section - menu section containing array of links / toggles
 * @return - the respective array of links / toggles to be rendered
 */
const getMenuItems = (
  section: FluidContextMenuSection
): FluidContextMenuLink[] | FluidContextMenuToggle[] => {
  switch (section.type) {
    case FluidContextMenuItemType.LINK:
      return section.links;
    case FluidContextMenuItemType.TOGGLE:
    case FluidContextMenuItemType.TOGGLE_GROUP:
      return section.toggles;
  }
};
